import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useRouteParams } from "@vueuse/router";
import { useMetadataExperienceApi } from "@/composables/useMetadataExperienceApi";
import { mapManageableHighlightToPremadeHighlight } from "@/features/experience-raw/lib/map-premade-highlights";
import {
  commitCustomHighlights,
  type CustomHighlightOptions,
} from "@/features/experience-highlights/lib/commit-custom-highlights";
import { useCurationHighlights } from "@/features/experience-curation/sections/content-generation/highlights/useCurationHighlights";
import { useCurationIncluded } from "@/features/experience-curation/sections/content-generation/highlights/useCurationIncluded";
import { useCurationNonIncluded } from "@/features/experience-curation/sections/content-generation/highlights/useCurationNonIncluded";
import { useCurationImportantInformation } from "@/features/experience-curation/sections/content-generation/highlights/useCurationImportantInformation";
import {
  useCurrentExperienceHighlightsQuery,
  EXPERIENCE_HIGHLIGHTS_QUERY_KEY,
} from "@/features/experience-curation/queries/useExperienceHighlightsQuery";
import { FlowCode, StatusCode } from "@/types/generated/ContentCommandApi";
import type { HighlightsKey } from "@/types/Highlights";

/**
 * Mutation responsible for persisting all four highlight types (highlights,
 * included, non_included, important_information) — both premade and custom.
 *
 * Premade highlights are saved via the metadata-experience API.
 * Custom highlights are saved via the content-command API.
 */
export function useCurationHighlightsSaveMutation() {
  const experienceId = useRouteParams<string>("id");
  const queryClient = useQueryClient();
  const { put, post, del } = useMetadataExperienceApi();

  const highlightsStore = useCurationHighlights();
  const includedStore = useCurationIncluded();
  const nonIncludedStore = useCurationNonIncluded();
  const importantInformationStore = useCurationImportantInformation();

  const highlightsQuery = useCurrentExperienceHighlightsQuery("highlights");
  const includedQuery = useCurrentExperienceHighlightsQuery("included");
  const nonIncludedQuery = useCurrentExperienceHighlightsQuery("non_included");
  const importantInformationQuery = useCurrentExperienceHighlightsQuery("important_information");

  const metadataEndpoints: Record<HighlightsKey, string> = {
    highlights: "experience-highlights",
    included: "experience-included",
    non_included: "experience-non-included",
    important_information: "experience-important-information",
  };

  const metadataFieldKeys: Record<HighlightsKey, string> = {
    highlights: "highlights",
    included: "included",
    non_included: "non_included",
    important_information: "important_information",
  };

  async function savePremade(fieldKey: HighlightsKey, premadeCodes: string[], existingId: string | undefined) {
    const endpoint = metadataEndpoints[fieldKey];
    const payloadKey = metadataFieldKeys[fieldKey];
    const payload = {
      experience_id: experienceId.value,
      [payloadKey]: premadeCodes,
    };

    const hasContent = premadeCodes.length > 0;

    if (hasContent && existingId) {
      await put(`${endpoint}/${existingId}`, { ...payload, language_code: "en" });
    } else if (hasContent && !existingId) {
      await post(endpoint, { ...payload, language_code: "en" });
    } else if (!hasContent && existingId) {
      await del(`${endpoint}/${existingId}`);
    }
  }

  return useMutation({
    mutationFn: async () => {
      const statusCode = StatusCode.IN_REVIEW;
      const flowCode = FlowCode.CURATION;

      const customHighlightOptions: CustomHighlightOptions = {
        curationFlowCode: flowCode,
        toBeEditedStatusCode: statusCode,
      };

      const stores = [
        { store: highlightsStore, query: highlightsQuery, key: "highlights" as const },
        { store: includedStore, query: includedQuery, key: "included" as const },
        { store: nonIncludedStore, query: nonIncludedQuery, key: "non_included" as const },
        { store: importantInformationStore, query: importantInformationQuery, key: "important_information" as const },
      ];

      await Promise.all(
        stores.flatMap(({ store, query, key }) => {
          if (!store.hasChanges) return [];

          const premadeCodes = mapManageableHighlightToPremadeHighlight(store.model.premade);
          const existingId = query.data.value?.id;

          return [
            savePremade(key, premadeCodes, existingId),
            commitCustomHighlights(experienceId.value, key, store.model.custom, customHighlightOptions),
          ];
        })
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [EXPERIENCE_HIGHLIGHTS_QUERY_KEY] });

      highlightsStore.reset();
      includedStore.reset();
      nonIncludedStore.reset();
      importantInformationStore.reset();
    },
  });
}
